import React, { useState } from 'react';
import {
  useNavigate,
  Link as RouterLink } from 'react-router-dom';
import axios from '../services/api';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Input,
  Button,
  Link,
  Icon,
  Image,
  Alert,
  Field,
} from '@chakra-ui/react';
import { InputGroup, InputLeftElement } from '@/components/ui/input-group';
import { FaEnvelope, FaArrowLeft } from 'react-icons/fa';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!email) {
      newErrors.email = 'El email es obligatorio';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'El email no tiene un formato válido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});
    
    try {
      await axios.post('/auth/forgot-password', { email });
      setEmailSent(true);
    } catch (error) {
      console.error('Error al solicitar reset:', error);
      // Siempre mostramos un mensaje genérico por seguridad
      setEmailSent(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box minH="100vh" bgGradient="linear(to-br, purple.50, white, gold.50)">
      {/* Header simple */}
      <Box bg="white" boxShadow="sm" py={5}>
        <Container maxW="7xl">
          <HStack justify="space-between">
            <HStack 
              gap={3}
              cursor="pointer"
              onClick={() => navigate('/')}
            >
              <Image 
                src="/logo y texto cadencya.png" 
                alt="Cadencya" 
                h={{ base: "55px", md: "70px" }}
                w="auto"
              />
            </HStack>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/login')}
              fontWeight="600"><Icon asChild><FaArrowLeft /></Icon>Volver al login
                          </Button>
          </HStack>
        </Container>
      </Box>

      {/* Contenido principal */}
      <Container maxW="md" py={{ base: 8, md: 16 }}>
        <VStack gap={{ base: 8, md: 12 }}>
          {/* Formulario */}
          <Box
            bg="white"
            p={{ base: 8, md: 10 }}
            rounded="3xl"
            boxShadow="xl"
            w="full"
          >
            {!emailSent ? (
              <>
                <VStack gap={6} mb={8}>
                  <Image
                    src="/capirote-amarillo.png"
                    alt="Recuperar contraseña"
                    maxW="120px"
                  />
                  <VStack gap={2}>
                    <Heading size="xl" textAlign="center">
                      ¿Olvidaste tu contraseña?
                    </Heading>
                    <Text color="gray.600" textAlign="center">
                      Introduce tu email y te enviaremos un enlace para restablecerla
                    </Text>
                  </VStack>
                </VStack>

                <form onSubmit={handleSubmit}>
                  <VStack gap={5}>
                    {/* Email */}
                    <Field.Root invalid={!!errors.email}>
                      <Field.Label fontWeight="600">Email</Field.Label>
                      <InputGroup size="lg">
                        <InputLeftElement>
                          <Icon color="gray.400" asChild><FaEnvelope /></Icon>
                        </InputLeftElement>
                        <Input
                          type="email"
                          placeholder="tu@email.com"
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            if (errors.email) {
                              setErrors({ ...errors, email: '' });
                            }
                          }}
                          rounded="xl"
                          bg="gray.50"
                          border="2px solid"
                          borderColor="gray.200"
                          _hover={{ borderColor: 'gray.300' }}
                          _focus={{ borderColor: 'purple.500', bg: 'white' }}
                        />
                      </InputGroup>
                      <Field.ErrorText>{errors.email}</Field.ErrorText>
                    </Field.Root>

                    {/* Botón de enviar */}
                    <Button
                      type="submit"
                      bg="purple.600"
                      color="white"
                      size="lg"
                      w="full"
                      h="14"
                      rounded="full"
                      loading={isLoading}
                      loadingText="Enviando..."
                      fontSize="lg"
                      fontWeight="700"
                      _hover={{ 
                        bg: 'purple.700',
                        transform: 'scale(1.02)',
                      }}
                      _active={{ transform: 'scale(0.98)' }}
                    >
                      Enviar enlace de recuperación
                    </Button>

                    {/* Link a login */}
                    <Text fontSize="sm" color="gray.600" textAlign="center" pt={2}>
                      ¿Ya tienes tu contraseña?{' '}
                      <Link
                        color="purple.600"
                        fontWeight="600"
                        _hover={{ textDecoration: "underline" }}
                        asChild><RouterLink to="/login">Iniciar sesión
                                              </RouterLink></Link>
                    </Text>
                  </VStack>
                </form>
              </>
            ) : (
              <VStack gap={6}>
                <Image
                  src="/capirote-amarillo.png"
                  alt="Email enviado"
                  maxW="120px"
                />
                <Alert.Root
                  status="success"
                  variant="subtle"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  textAlign="center"
                  rounded="xl"
                  p={6}
                >
                  <Alert.Indicator boxSize="40px" mr={0} />
                  <Heading size="md" mt={4} mb={2}>
                    ¡Revisa tu correo!
                  </Heading>
                  <Alert.Description maxW="sm" fontSize="md">
                    Si existe una cuenta asociada a <strong>{email}</strong>, recibirás un correo con instrucciones para restablecer tu contraseña.
                  </Alert.Description>
                </Alert.Root>

                <VStack gap={3} pt={4} w="full">
                  <Text fontSize="sm" color="gray.600" textAlign="center">
                    El enlace será válido durante 1 hora.
                  </Text>
                  <Button
                    variant="outline"
                    colorPalette="purple"
                    size="lg"
                    w="full"
                    rounded="full"
                    onClick={() => navigate('/login')}
                  >
                    Volver al login
                  </Button>
                </VStack>
              </VStack>
            )}
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default ForgotPassword;
